import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play } from 'lucide-react';
import { useSeo } from '../hooks/useSeo';
import { PAGE_SEO } from '../seo/site';
import { Reveal } from '../components/Reveal';
import { ContactForm, ContactAside, WHATSAPP_URL } from '../components/ContactForm';
import { WhatsAppIcon, Star } from '../components/icons/WhatsAppIcon';

const heroImages = [
  { src: '/hero-3.jpg', alt: 'Einrichtung eines Kassensystems' },
  { src: '/hero-2.jpg', alt: 'Persönliche Beratung vor Ort' },
  { src: '/hero-1.jpg', alt: 'Beratungsgespräch am Kassensystem' },
];

const usps = ['Kostenlose Beratung', 'Unabhängig', 'Persönlicher Support'];

const problems = [
  'Diverse Anbieter – doch keiner berät wirklich objektiv.',
  'Welches Werkzeug ist das richtige und wer verknüpft sie miteinander?',
  'Support kostet Zeit – und bei Problemen steht der Betrieb.',
];

const solutions = ['Tool-Auswahl', 'Setup & Schulung', 'Support & Betrieb'];

const processSteps = [
  { title: 'Kennenlernen', description: 'In einem persönlichen Gespräch verstehen wir deine Situation, deine Ziele und deine aktuellen Herausforderungen.' },
  { title: 'Empfehlung', description: 'Wir analysieren deinen Bedarf und präsentieren dir dein digitales Ökosystem – transparent, verständlich und auf den Punkt.' },
  { title: 'Umsetzung & Support', description: 'Wir begleiten dich bei der Einführung und stehen dir danach mit unserer Plattform und mit Premium-Support zur Seite.' },
];

const testimonials = [
  { videoId: 'P3GVRaTQqyA', name: 'Mimosas Brunch Club', subtitle: 'Café & Catering' },
  { videoId: 'GsS2yH7azy8', name: 'La Casita Azul', subtitle: 'Restaurant' },
  { videoId: 'jcy3wdfxT84', name: 'Das Pulverfass', subtitle: 'Cabaret Theater' },
];

const whyUsArguments = [
  { title: 'Unabhängig & ehrlich', description: 'Wir sind kein generisches Vergleichsportal. Wir empfehlen nur, was wirklich zu dir passt.' },
  { title: 'Schneller Support', description: 'Mit unserem Premium-Support-Abo bekommst du schnelle und ausgezeichnete Hilfe für alles Digitale an einem Ort.' },
  { title: 'Alles aus einer Hand', description: 'Von der Auswahl über die Einrichtung bis zur laufenden Betreuung – wir sind dein digitaler Partner.' },
];

const HeroPolaroid = ({ index, onSelect }) => (
  <figure className="polaroid polaroid--r1 max-w-[460px] mx-auto lg:ml-auto lg:mr-0" data-testid="hero-polaroid">
    <div className="polaroid__media aspect-[3/2]">
      {heroImages.map((image, i) => (
        <img
          key={image.src}
          src={image.src}
          alt={image.alt}
          width="1080"
          height="720"
          loading={i === 0 ? 'eager' : 'lazy'}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === index ? 'opacity-100' : 'opacity-0'}`}
          data-testid={`hero-image-${i}`}
        />
      ))}
    </div>
    <figcaption className="flex items-center justify-between gap-4">
      <span className="polaroid__name text-[1.25rem]">{heroImages[index].alt}</span>
      <span className="flex gap-2 flex-shrink-0">
        {heroImages.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onSelect(i)}
            className={`w-3 h-3 border-2 border-ink transition-colors ${i === index ? 'bg-purple' : 'bg-off hover:bg-lilac'}`}
            aria-label={`Bild ${i + 1} anzeigen`}
          />
        ))}
      </span>
    </figcaption>
  </figure>
);

const TestimonialPolaroid = ({ t, index, playing, onPlay }) => (
  <figure className={`polaroid polaroid--r${index + 1}`} data-testid={`testimonial-${index}`}>
    <div className="polaroid__media polaroid__media--portrait">
      {playing ? (
        <iframe
          src={`https://www.youtube.com/embed/${t.videoId}?autoplay=1`}
          title={t.name}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <>
          <img
            src={`https://img.youtube.com/vi/${t.videoId}/maxresdefault.jpg`}
            alt={t.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <button
            type="button"
            onClick={onPlay}
            className="absolute inset-0 flex items-center justify-center bg-ink/30 hover:bg-ink/40 transition-colors group"
            aria-label={`Video-Testimonial abspielen: ${t.name}`}
            data-testid={`play-video-${index}`}
          >
            <span className="polaroid__play group-hover:scale-110">
              <Play className="w-6 h-6 ml-1 text-ink" fill="#17102B" />
            </span>
          </button>
        </>
      )}
    </div>
    <figcaption>
      <span className="polaroid__name block">{t.name}</span>
      <div className="polaroid__cat">{t.subtitle}</div>
    </figcaption>
  </figure>
);

const HomePage = () => {
  useSeo(PAGE_SEO.home);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div data-testid="home-page">
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-off py-16 md:py-20 lg:py-28" data-testid="hero-section">
        <div className="hero-half hidden lg:block" aria-hidden="true" />
        <Reveal className="wrap relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="eyebrow mb-6 md:mb-8"><Star />Unabhängige Beratung für Gastronomie</p>
            <h1 className="display-1 max-w-[24ch] mb-8 md:mb-10">
              Individuell und ganzheitlich betreut: <span className="serif-it text-purple">Persönliche</span> Beratung, Einrichtung und Support.
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 max-w-[62rem] mb-8 md:mb-10">
              <p className="md:col-span-12 text-lg">
                Wir analysieren deinen Bedarf, empfehlen die richtigen Systeme und richten diese für dich ein.
              </p>
              <p className="md:col-span-12 text-lg">
              Melde dich gern bei uns für ein persönliches Gespräch. Du kannst auch unsere digitale Erstberatung nutzen oder dich für unsere Beratungsplattform registrieren.
              </p>
            </div>
            <div className="cta-row mb-8 md:mb-10">
              <Link to="/register" className="btn btn--purple" data-testid="hero-cta-platform">Plattform entdecken</Link>
              <Link to="/erstberatung" className="btn" data-testid="hero-cta-erstberatung">Erstberatung</Link>
            </div>
            <div className="flex flex-wrap gap-3">
              {usps.map((usp, index) => (
                <span key={usp} className="badge-ink" data-testid={`usp-${index}`}><Star />{usp}</span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4">
            <HeroPolaroid index={currentImageIndex} onSelect={setCurrentImageIndex} />
          </div>
        </Reveal>
      </section>

      {/* ================= PROBLEM / LÖSUNG ================= */}
      <section className="sec sec--deep" id="loesungen" data-testid="partner-section">
        <Reveal className="wrap">
          <h2 className="display-2 max-w-[22ch] mb-5">
            Der Partner für alle <span className="serif-it text-lilac">digitalen</span> Lösungen in der Gastronomie
          </h2>
          <p className="text-lg max-w-[56ch] opacity-90 mb-12 md:mb-16">
            Wir bringen Ordnung in deine Systemlandschaft – mit klaren Empfehlungen, sauberer Einrichtung und einem Ansprechpartner, der wirklich hilft.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start mb-12 md:mb-16">
            <div className="lg:col-span-7" data-testid="problem-card">
              <p className="label-caps text-lilac mb-5">Das Problem</p>
              <ul className="rule-list rule-list--off">
                {problems.map((p) => (
                  <li key={p} className="flex gap-4 items-start font-display font-semibold text-xl md:text-2xl leading-snug py-5">
                    <span className="text-lilac font-extrabold flex-none" aria-hidden="true">✕</span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-5 card-ink card-ink--off p-7 md:p-10" data-testid="solution-card">
              <p className="label-caps text-purple mb-5">Unsere Lösung</p>
              <p className="mb-6">
                Wir empfehlen die richtigen Systeme, richten sie ein und bleiben als Ansprechpartner an deiner Seite – schnell, unabhängig und übersichtlich.
              </p>
              <ul className="grid gap-3.5">
                {solutions.map((s, index) => (
                  <li key={s} className="flex gap-3 items-baseline font-display font-bold text-lg" data-testid={`solution-${index}`}>
                    <Star />{s}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="cta-row">
            <Link to="/erstberatung" className="btn btn--lilac" data-testid="partner-cta-beratung">Kostenlose Beratung starten</Link>
            <Link to="/register" className="btn btn--ghost-off" data-testid="partner-cta-platform">Plattform entdecken</Link>
          </div>
        </Reveal>
      </section>

      {/* ================= ABLAUF ================= */}
      <section className="sec bg-off" data-testid="process-section">
        <Reveal className="wrap">
          <h2 className="display-2 max-w-[20ch] mb-4">
            Dein Weg zu <span className="serif-it text-purple">reibungslosem</span> Einsatz neuer Tools
          </h2>
          <p className="text-lg mb-12 md:mb-16">Einfach, strukturiert und auf deine Bedürfnisse zugeschnitten</p>

          <div className="grid grid-cols-1 md:grid-cols-3 mb-12 md:mb-16">
            {processSteps.map((step, index) => (
              <div
                key={step.title}
                className="border-ink border-t md:border-t-0 md:border-l first:border-t-0 md:first:border-l-0 py-8 first:pt-0 last:pb-0 md:py-0 md:px-8 md:first:pl-0 md:last:pr-0"
                data-testid={`process-step-${index}`}
              >
                <span className="step-num mb-5" aria-hidden="true">{index + 1}</span>
                <h3 className="font-display font-bold text-2xl mb-2.5">{step.title}</h3>
                <p className="text-[0.98rem]">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="cta-row">
            <Link to="/erstberatung" className="btn btn--purple" data-testid="process-cta-termin">Beratungstermin</Link>
            <Link to="/erstberatung" className="btn btn--ghost" data-testid="process-cta-digital">Digitale Erstberatung</Link>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn btn--wa" data-testid="process-cta-whatsapp">
              <WhatsAppIcon />
              WhatsApp
            </a>
          </div>
        </Reveal>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <section className="sec sec--lilac" data-testid="testimonials-section">
        <Reveal className="wrap">
          <h2 className="display-2 max-w-[20ch] mb-4">
            Das sagen unsere <span className="serif-it text-deep">Kund:innen</span>
          </h2>
          <p className="text-lg mb-12 md:mb-16">Authentische Erfahrungen von Gastronom:innen, die bereits mit uns arbeiten</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 max-w-[360px] md:max-w-[1040px] mx-auto md:mx-0 items-start">
            {testimonials.map((t, index) => (
              <TestimonialPolaroid
                key={t.videoId}
                t={t}
                index={index}
                playing={playingVideo === index}
                onPlay={() => setPlayingVideo(index)}
              />
            ))}
          </div>
        </Reveal>
      </section>

      {/* ================= WARUM ================= */}
      <section className="sec sec--purple" id="warum" data-testid="why-us-section">
        <Reveal className="wrap">
          <h2 className="display-2 mb-10 md:mb-16">Warum gastro-beratung.de?</h2>
          <div>
            {whyUsArguments.map((arg, index) => (
              <div
                key={arg.title}
                className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-14 items-start py-8 md:py-12 border-t-2 border-off last:border-b-2"
                data-testid={`why-us-${index}`}
              >
                <div className="flex gap-4 items-start">
                  <span className="text-lilac text-2xl md:text-3xl leading-tight flex-none" aria-hidden="true">✳</span>
                  <h3 className="font-display font-bold text-2xl md:text-4xl leading-tight">{arg.title}</h3>
                </div>
                <p className="text-lg opacity-90 max-w-[52ch]">{arg.description}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ================= KONTAKT ================= */}
      <section className="sec bg-off" id="kontakt" data-testid="contact-section">
        <Reveal className="wrap">
          <h2 className="display-2 mb-10 md:mb-16">Kontaktiere uns</h2>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <ContactAside
                intro="Melde dich jetzt direkt bei uns für ein persönliches Gespräch – wir antworten in der Regel innerhalb eines Werktags."
                title="Oder direkt per WhatsApp Kontakt aufnehmen"
              />
            </div>
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default HomePage;
